import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, Loader2, RefreshCw, AlertTriangle, CheckCircle, Activity, Wallet, Search, Plug,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { apiClient, extractErrorMessage } from '@/lib/api';
import { VehiclePicker } from '@/components/common/VehiclePicker';

// PR-PETROAPP MAY 12 — admin page for the real-time PetroApp integration.
// Three panels:
//   1. Connection status — configured? last sync? wallet balance? + Sync now button
//   2. Recent runs — last 20 sync runs from integration_sync
//   3. Orphans — transactions the matcher couldn't tie to a vehicle, with a
//      vehicle picker per row so the operator can resolve them inline.
//
// Permission: super_admin / admin / accountant (server enforces; this page
// is just rendered when /health doesn't 403).

interface Health {
  configured: boolean;
  lastSuccessfulSync: string | null;
  lastSuccessfulInsert: number | null;
  pollIntervalMinutes: number;
}

interface Run {
  id: string;
  sync_type: string;
  started_at: string;
  completed_at: string | null;
  records_pulled: number | null;
  records_created: number | null;
  records_updated: number | null;
  records_failed: number | null;
  cursor_value: string | null;
  error_message: string | null;
}

interface Orphan {
  id: string;
  petroapp_card_id: string;
  card_number: string | null;
  vehicle_plate: string | null;
  driver_name: string | null;
  last_synced_at: string | null;
}

interface Balance {
  balance_sar: number | null;
  currency: string | null;
  cached: boolean;
}

function fmtAgo(iso: string | null): string {
  if (!iso) return '—';
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return Math.round(ms / 1000) + 's ago';
  if (ms < 3_600_000) return Math.round(ms / 60_000) + ' min ago';
  if (ms < 86_400_000) return Math.round(ms / 3_600_000) + 'h ago';
  return Math.round(ms / 86_400_000) + ' days ago';
}

export default function PetroAppPage() {
  const [health, setHealth] = useState<Health | null>(null);
  const [balance, setBalance] = useState<Balance | null>(null);
  const [balanceError, setBalanceError] = useState<string | null>(null);
  const [runs, setRuns] = useState<Run[]>([]);
  const [orphans, setOrphans] = useState<Orphan[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const [h, r, o] = await Promise.all([
        apiClient.get<Health>('/api/v1/petroapp/health'),
        apiClient.get<{ items: Run[] }>('/api/v1/petroapp/runs'),
        apiClient.get<{ items: Orphan[] }>('/api/v1/petroapp/orphans'),
      ]);
      setHealth(h.data);
      setRuns(r.data.items ?? []);
      setOrphans(o.data.items ?? []);
      setError(null);
    } catch (e) { setError(extractErrorMessage(e)); }
    finally { setLoading(false); }
    // Balance is gated by token presence, fetch separately so a 503 doesn't
    // blank the rest of the page.
    try {
      const b = await apiClient.get<Balance>('/api/v1/petroapp/balance');
      setBalance(b.data);
      setBalanceError(null);
    } catch (e) { setBalanceError(extractErrorMessage(e)); setBalance(null); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  async function triggerSync() {
    setSyncing(true); setSyncResult(null); setError(null);
    try {
      const r = await apiClient.post<{ fetched: number; newRows: number; duplicates: number; orphans: number }>(
        '/api/v1/petroapp/sync', {},
      );
      setSyncResult(`Fetched ${r.data.fetched}, new ${r.data.newRows}, dup ${r.data.duplicates}, orphans ${r.data.orphans}`);
      await refresh();
    } catch (e) { setError(extractErrorMessage(e)); }
    finally { setSyncing(false); }
  }

  const connectedClass = health?.configured
    ? 'text-emerald-700 dark:text-emerald-400'
    : 'text-amber-700 dark:text-amber-400';

  return (
    <div className="min-h-screen bg-background pb-24">
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/dashboard"><Button variant="ghost" size="sm" className="gap-2"><ArrowLeft className="h-4 w-4" /> Home</Button></Link>
          <div className="text-sm font-medium inline-flex items-center gap-2">
            <Plug className="h-4 w-4" /> PetroApp integration
          </div>
          <Button onClick={triggerSync} disabled={syncing || !health?.configured} size="sm" className="gap-2">
            {syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            Sync now
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-4 space-y-4">
        {error && (
          <div className="rounded-md bg-rose-500/10 border border-rose-500/30 text-rose-700 dark:text-rose-400 p-3 text-sm inline-flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" /> {error}
          </div>
        )}
        {syncResult && (
          <div className="rounded-md bg-emerald-500/10 border border-emerald-500/30 text-emerald-700 dark:text-emerald-400 p-3 text-sm inline-flex items-center gap-2">
            <CheckCircle className="h-4 w-4" /> {syncResult}
          </div>
        )}

        {/* Connection status */}
        <div className="rounded-lg border border-border p-4 space-y-3">
          <div className="text-xs uppercase tracking-wider text-muted-foreground">Connection status</div>
          {loading ? (
            <div className="text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin inline" /> Loading…</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="rounded-md border border-border p-3">
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Token</div>
                <div className={'text-lg font-medium mt-0.5 inline-flex items-center gap-2 ' + connectedClass}>
                  {health?.configured ? <><CheckCircle className="h-4 w-4" /> Configured</> : <><AlertTriangle className="h-4 w-4" /> Not configured</>}
                </div>
              </div>
              <div className="rounded-md border border-border p-3">
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Wallet balance</div>
                <div className="text-lg font-medium mt-0.5 inline-flex items-center gap-2">
                  <Wallet className="h-4 w-4 text-muted-foreground" />
                  {balance?.balance_sar != null
                    ? `${balance.balance_sar.toLocaleString(undefined, { minimumFractionDigits: 2 })} ${balance.currency ?? 'SAR'}`
                    : balanceError ? <span className="text-xs text-muted-foreground italic">{balanceError}</span>
                    : <span className="text-xs text-muted-foreground italic">—</span>}
                </div>
              </div>
              <div className="rounded-md border border-border p-3">
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Last sync</div>
                <div className="text-lg font-medium mt-0.5 inline-flex items-center gap-2">
                  <Activity className="h-4 w-4 text-muted-foreground" />
                  {fmtAgo(health?.lastSuccessfulSync ?? null)}
                </div>
                <div className="text-[10px] text-muted-foreground mt-0.5">
                  next auto-sync ≤ {health?.pollIntervalMinutes ?? 15} min
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Recent runs */}
        <div className="rounded-lg border border-border overflow-hidden">
          <div className="px-3 py-2 text-xs uppercase tracking-wider text-muted-foreground bg-muted/30 border-b border-border">
            Recent sync runs ({runs.length})
          </div>
          {runs.length === 0 ? (
            <div className="p-4 text-sm text-muted-foreground text-center">No runs yet.</div>
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-muted/20 text-muted-foreground"><tr>
                <th className="text-left px-3 py-2 font-normal">Started</th>
                <th className="text-left px-3 py-2 font-normal">Trigger</th>
                <th className="text-right px-3 py-2 font-normal">Pulled</th>
                <th className="text-right px-3 py-2 font-normal">New</th>
                <th className="text-right px-3 py-2 font-normal">Dup</th>
                <th className="text-right px-3 py-2 font-normal">Failed</th>
                <th className="text-left px-3 py-2 font-normal">Status</th>
              </tr></thead>
              <tbody>
                {runs.map((r) => (
                  <tr key={r.id} className="border-t border-border">
                    <td className="px-3 py-2 whitespace-nowrap">{new Date(r.started_at).toLocaleString('en-GB')}</td>
                    <td className="px-3 py-2">{r.sync_type}</td>
                    <td className="px-3 py-2 text-right font-mono">{r.records_pulled ?? '—'}</td>
                    <td className="px-3 py-2 text-right font-mono text-emerald-700 dark:text-emerald-400">{r.records_created ?? '—'}</td>
                    <td className="px-3 py-2 text-right font-mono text-muted-foreground">{r.records_updated ?? '—'}</td>
                    <td className="px-3 py-2 text-right font-mono text-rose-700 dark:text-rose-400">{r.records_failed ?? '—'}</td>
                    <td className="px-3 py-2">
                      {r.error_message ? (
                        <span className="text-rose-700 dark:text-rose-400" title={r.error_message}>
                          failed
                        </span>
                      ) : r.completed_at ? (
                        <span className="text-emerald-700 dark:text-emerald-400">success</span>
                      ) : (
                        <span className="text-amber-700 dark:text-amber-400">running</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Orphans */}
        <OrphansPanel orphans={orphans} onLinked={refresh} />
      </main>
    </div>
  );
}

function OrphansPanel({ orphans, onLinked }: { orphans: Orphan[]; onLinked: () => Promise<void> | void }) {
  const [busy, setBusy] = useState<string | null>(null);

  async function link(orphanId: string, vehicleId: string | null) {
    if (!vehicleId) return;
    setBusy(orphanId);
    try {
      await apiClient.patch('/api/v1/petroapp/orphans/' + orphanId, { vehicleId });
      await onLinked();
    } catch (e) {
      window.alert(extractErrorMessage(e));
    } finally {
      setBusy(null);
    }
  }

  if (orphans.length === 0) {
    return (
      <div className="rounded-lg border border-border p-4 text-sm text-muted-foreground text-center">
        <Search className="h-4 w-4 inline mr-1" /> No orphan transactions — every PetroApp row matched to a vehicle.
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-border overflow-hidden">
      <div className="px-3 py-2 text-xs uppercase tracking-wider text-muted-foreground bg-muted/30 border-b border-border">
        Orphans — need manual link ({orphans.length})
      </div>
      <div className="divide-y divide-border">
        {orphans.map((o) => (
          <OrphanRow key={o.id} orphan={o} onLink={(vid) => link(o.id, vid)} busy={busy === o.id} />
        ))}
      </div>
    </div>
  );
}

function OrphanRow({ orphan, onLink, busy }: { orphan: Orphan; onLink: (v: string | null) => void; busy: boolean }) {
  const [picked, setPicked] = useState<string | null>(null);
  return (
    <div className="grid grid-cols-1 md:grid-cols-12 gap-2 p-3 items-center">
      <div className="md:col-span-3">
        <div className="text-sm">Card #{orphan.card_number ?? '—'}</div>
        <div className="text-xs text-muted-foreground">{orphan.driver_name ?? 'unknown driver'}</div>
      </div>
      <div className="md:col-span-3">
        <div className="text-sm">Plate: {orphan.vehicle_plate ?? '—'}</div>
        <div className="text-xs text-muted-foreground">{orphan.last_synced_at ? new Date(orphan.last_synced_at).toLocaleString('en-GB') : '—'}</div>
      </div>
      <div className="md:col-span-4">
        <VehiclePicker
          value={picked}
          onChange={setPicked}
          placeholder="Link to vehicle…"
        />
      </div>
      <div className="md:col-span-2 flex justify-end">
        <Button size="sm" onClick={() => onLink(picked)} disabled={!picked || busy} className="gap-2">
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle className="h-3 w-3" />}
          Link
        </Button>
      </div>
    </div>
  );
}
